import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import Button from '../ui/Button';
import { typography } from '../../design-system/tokens';
import { computeTooltipPlacement } from '../../utils/tourTooltipPosition';
import { cn } from '../../design-system/cn';

const PAD = 10;
const DESKTOP_CARD_WIDTH = 340;
const ARROW_SIZE = 12;

function isCenterStep(step) {
  return !step?.tourId || step.placement === 'center';
}

function readSafeInsetPx(name) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  } catch (_) {
    return 0;
  }
}

/**
 * Reserve space so the card never covers the sticky mobile header (wallet
 * button) or the bottom nav, and honour iPhone/PWA safe areas.
 */
function getSafeInsets(mobile) {
  const safeTop = readSafeInsetPx('--safe-top');
  const safeBottom = readSafeInsetPx('--safe-bottom');
  if (mobile) {
    return {
      top: 56 + safeTop,
      bottom: 88 + safeBottom,
      left: 8,
      right: 8,
    };
  }
  return { top: 8, bottom: 8, left: 8, right: 8 };
}

/** First visible element matching the step's data-tour-id. */
function findTourTarget(step) {
  if (!step?.tourId || typeof document === 'undefined') return null;
  const nodes = document.querySelectorAll(`[data-tour-id="${step.tourId}"]`);
  for (let i = 0; i < nodes.length; i += 1) {
    const rect = nodes[i].getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return nodes[i];
  }
  return null;
}

function TooltipArrow({ arrow }) {
  if (!arrow) return null;
  const base = {
    position: 'absolute',
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    background: '#030712',
    borderColor: 'rgba(16,185,129,0.4)',
    borderStyle: 'solid',
    transform: 'rotate(45deg)',
  };
  if (arrow.side === 'top') {
    return <span aria-hidden style={{ ...base, top: -ARROW_SIZE / 2, left: arrow.offset - ARROW_SIZE / 2, borderWidth: '1px 0 0 1px' }} />;
  }
  if (arrow.side === 'bottom') {
    return <span aria-hidden style={{ ...base, bottom: -ARROW_SIZE / 2, left: arrow.offset - ARROW_SIZE / 2, borderWidth: '0 1px 1px 0' }} />;
  }
  if (arrow.side === 'left') {
    return <span aria-hidden style={{ ...base, left: -ARROW_SIZE / 2, top: arrow.offset - ARROW_SIZE / 2, borderWidth: '0 0 1px 1px' }} />;
  }
  return <span aria-hidden style={{ ...base, right: -ARROW_SIZE / 2, top: arrow.offset - ARROW_SIZE / 2, borderWidth: '1px 1px 0 0' }} />;
}

function InstructionCard({
  step,
  stepIndex,
  totalSteps,
  isFirst,
  isLast,
  onNext,
  onBack,
  onSkip,
  onDone,
  targetRect,
}) {
  const isCenter = isCenterStep(step) || !targetRect;
  const cardRef = useRef(null);
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });

  const mobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const baseWidth = mobile
    ? Math.min(340, (typeof window !== 'undefined' ? window.innerWidth : 360) - 32)
    : DESKTOP_CARD_WIDTH;

  // Measure the rendered card so collision detection uses the real height.
  useLayoutEffect(() => {
    if (!cardRef.current) return undefined;
    const r = cardRef.current.getBoundingClientRect();
    if (Math.abs(r.height - cardSize.height) > 1 || Math.abs(r.width - cardSize.width) > 1) {
      setCardSize({ width: r.width, height: r.height });
    }
    return undefined;
  }, [step?.id, stepIndex, targetRect, cardSize.height, cardSize.width]);

  const placement = useMemo(() => {
    if (isCenter || !targetRect || typeof window === 'undefined') return null;
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    return computeTooltipPlacement({
      targetRect,
      card: { width: baseWidth, height: cardSize.height || 220 },
      viewport,
      safeInsets: getSafeInsets(mobile),
    });
  }, [isCenter, targetRect, baseWidth, cardSize.height, mobile]);

  // Debug: report the geometry decision for each positioned step.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    if (!placement || !targetRect) return undefined;
    console.log('[onboarding-position]', {
      step: step?.id,
      target: {
        top: Math.round(targetRect.top),
        left: Math.round(targetRect.left),
        width: Math.round(targetRect.width),
        height: Math.round(targetRect.height),
      },
      tooltip: {
        top: Math.round(placement.top),
        left: Math.round(placement.left),
        width: Math.round(placement.width),
        height: Math.round(cardSize.height),
      },
      placement: placement.placement,
      fits: placement.fits,
    });
    return undefined;
  }, [placement, targetRect, step?.id, cardSize.height]);

  const isBottomSheet = placement?.placement === 'bottom-sheet';

  const cardStyle = isCenter || !placement
    ? {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: `min(${baseWidth}px, calc(100vw - 2rem))`,
        zIndex: 10062,
      }
    : {
        position: 'fixed',
        top: placement.top,
        left: placement.left,
        width: placement.width,
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 10062,
        ...(isBottomSheet ? {
          paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
        } : {}),
      };

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guided-tour-title"
      aria-describedby="guided-tour-body"
      data-testid={isBottomSheet ? 'guided-tour-bottom-sheet' : 'guided-tour-card'}
      className={cn(
        'rounded-2xl border border-emerald-500/40 bg-[#030712] shadow-[0_24px_70px_rgba(0,0,0,0.7)] p-4',
        isBottomSheet && 'guided-tour-bottom-sheet',
      )}
      style={cardStyle}
    >
      {!isCenter && placement && !isBottomSheet && <TooltipArrow arrow={placement.arrow} />}
      <div className="flex items-center justify-between mb-1">
        <p className={`${typography.microWide} text-emerald-400`}>
          Step {stepIndex + 1} of {totalSteps}
        </p>
        <button
          type="button"
          onClick={onSkip}
          className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-gray-300 bg-transparent border-0 cursor-pointer"
        >
          Skip
        </button>
      </div>
      <h2 id="guided-tour-title" className="text-lg font-black text-white mb-2">
        {step.title}
      </h2>
      <p id="guided-tour-body" className="text-sm text-gray-300 font-medium leading-relaxed mb-4">
        {step.body}
      </p>
      <div className="flex items-center gap-2">
        {!isFirst && (
          <Button type="button" variant="ghost" onClick={onBack} className="flex-1">
            Back
          </Button>
        )}
        {isLast ? (
          <Button type="button" variant="primary" onClick={onDone} className="flex-[2]">
            Done
          </Button>
        ) : (
          <Button type="button" variant="primary" onClick={onNext} className="flex-[2]">
            Next
          </Button>
        )}
      </div>
    </div>
  );
}

export default function GuidedSpotlightTour({
  open,
  steps = [],
  onComplete,
  onSkip,
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const settledRef = useRef(false);

  const step = steps[stepIndex] || null;

  // Find the next index (in either direction) whose target exists or is a center step.
  const resolveValidIndex = useCallback((startIndex, direction) => {
    for (let i = startIndex; i >= 0 && i < steps.length; i += direction) {
      const candidate = steps[i];
      if (isCenterStep(candidate) || findTourTarget(candidate)) return i;
    }
    return -1;
  }, [steps]);

  useEffect(() => {
    if (!open) return;
    settledRef.current = false;
    const first = resolveValidIndex(0, 1);
    setStepIndex(first === -1 ? 0 : first);
  }, [open, resolveValidIndex]);

  // Measure without forcing scroll. Used by resize / manual-scroll listeners.
  const measureOnly = useCallback(() => {
    if (!open || !step || isCenterStep(step)) {
      setTargetRect(null);
      return;
    }
    const el = findTourTarget(step);
    if (!el) {
      setTargetRect(null);
      return;
    }
    setTargetRect(el.getBoundingClientRect());
  }, [open, step]);

  // Resolve the step's target on step change: skip missing targets, and only
  // scroll the page when the target is actually off-screen.
  const resolveTargetForStep = useCallback(() => {
    if (!open || !step) {
      setTargetRect(null);
      return;
    }
    if (isCenterStep(step)) {
      setTargetRect(null);
      return;
    }
    const el = findTourTarget(step);
    if (!el) {
      // Target vanished — skip forward, then back, to the nearest valid step.
      const fwd = resolveValidIndex(stepIndex + 1, 1);
      const next = fwd === -1 ? resolveValidIndex(stepIndex - 1, -1) : fwd;
      if (next !== -1 && next !== stepIndex) setStepIndex(next);
      else setTargetRect(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const fullyVisible = rect.top >= 0 && rect.bottom <= viewportH;
    if (!fullyVisible) {
      // Scroll into view only when needed; re-measure after the scroll settles.
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      setTargetRect(rect);
      window.setTimeout(() => {
        const fresh = findTourTarget(step);
        if (fresh) setTargetRect(fresh.getBoundingClientRect());
      }, 320);
      return;
    }
    setTargetRect(rect);
  }, [open, step, stepIndex, resolveValidIndex]);

  useLayoutEffect(() => {
    resolveTargetForStep();
  }, [resolveTargetForStep]);

  // Recalculate position only on resize or manual scroll (no forced scrolling here).
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('resize', measureOnly);
    window.addEventListener('scroll', measureOnly, true);
    return () => {
      window.removeEventListener('resize', measureOnly);
      window.removeEventListener('scroll', measureOnly, true);
    };
  }, [open, measureOnly]);

  // Disable user-initiated scrolling while the overlay is active, but leave the
  // layout untouched (no overflow/padding mutation, so the background never
  // shifts) and still allow programmatic scrollIntoView for off-screen targets.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const preventGesture = (event) => {
      // Allow scrolling inside the instruction card itself if it ever overflows.
      if (event.target?.closest?.('[role="dialog"]')) return;
      event.preventDefault();
    };
    document.addEventListener('wheel', preventGesture, { passive: false });
    document.addEventListener('touchmove', preventGesture, { passive: false });
    return () => {
      document.removeEventListener('wheel', preventGesture);
      document.removeEventListener('touchmove', preventGesture);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onSkip?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onSkip]);

  const goNext = useCallback(() => {
    const next = resolveValidIndex(stepIndex + 1, 1);
    if (next === -1) {
      settledRef.current = true;
      onComplete?.();
      return;
    }
    setStepIndex(next);
  }, [resolveValidIndex, stepIndex, onComplete]);

  const goBack = useCallback(() => {
    const prev = resolveValidIndex(stepIndex - 1, -1);
    if (prev !== -1) setStepIndex(prev);
  }, [resolveValidIndex, stepIndex]);

  const goDone = useCallback(() => {
    settledRef.current = true;
    onComplete?.();
  }, [onComplete]);

  if (!open || !step || typeof document === 'undefined') return null;

  const isFirst = resolveValidIndex(stepIndex - 1, -1) === -1;
  const isLast = resolveValidIndex(stepIndex + 1, 1) === -1;
  const hasSpotlight = Boolean(targetRect) && !isCenterStep(step);

  const spotlightStyle = hasSpotlight ? {
    position: 'fixed',
    top: Math.max(8, targetRect.top - PAD),
    left: Math.max(8, targetRect.left - PAD),
    width: targetRect.width + PAD * 2,
    height: targetRect.height + PAD * 2,
    borderRadius: 16,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.88)',
    zIndex: 10058,
    pointerEvents: 'none',
  } : null;

  const ringStyle = hasSpotlight ? {
    position: 'fixed',
    top: Math.max(8, targetRect.top - PAD),
    left: Math.max(8, targetRect.left - PAD),
    width: targetRect.width + PAD * 2,
    height: targetRect.height + PAD * 2,
    borderRadius: 16,
    zIndex: 10060,
    pointerEvents: 'none',
  } : null;

  return createPortal(
    <div className="guided-tour-root" aria-hidden={false}>
      {!hasSpotlight && (
        <div className="fixed inset-0 bg-black/[0.88] z-[10058]" aria-hidden />
      )}
      {spotlightStyle && <div style={spotlightStyle} aria-hidden />}
      {ringStyle && <div className="guided-tour-ring" style={ringStyle} aria-hidden />}
      <InstructionCard
        step={step}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        isFirst={isFirst}
        isLast={isLast}
        onNext={goNext}
        onBack={goBack}
        onSkip={() => onSkip?.()}
        onDone={goDone}
        targetRect={targetRect}
      />
    </div>,
    document.body,
  );
}
